<template>
  <b-container
    fluid
    class="p-2 checkout-mobile-wrapper"
  >
    <!-- Encabezado -->
    <div class="invoice-header mb-3">
      <logo class="mb-2" />
      <div class="invoice-details">
        <h6 class="mb-1 font-weight-bold">
          {{ $t('invoiceTo') }}
        </h6>
        <p class="mb-0">
          {{ displayTaxInformation.corporate_name }}
        </p>
        <p class="mb-0 small">
          {{ $t('address') }}: {{ taxAddress }}
        </p>
        <p class="mb-0 small">
          RFC: {{ displayTaxInformation.rfc }}
        </p>
        <p class="mb-0 small">
          {{ $t('fiscalRegime') }}: {{ displayTaxInformation.fiscal_regime || '—' }}
        </p>
        <p class="mb-0 small">
          {{ $t('cfdiUse') }}: {{ displayTaxInformation.cfdi_use || '—' }}
        </p>
      </div>
    </div>

    <!-- Proyecto y fecha -->
    <b-card class="mb-3">
      <p class="mb-1 font-weight-bold">
        {{ project.name }}
      </p>
      <p class="mb-0 small">
        {{ $t('projectFolio') }}: #{{ project.folio }}
      </p>
      <p class="mb-0 small">
        {{ $t('quoteFolio') }}: #{{ quote.folio }}
      </p>
      <p class="mb-0 small">
        {{ $t('dateIssued') }}: {{ quote.published_at | date }}
      </p>
    </b-card>

    <!-- Dirección y total -->
    <b-card class="mb-3">
      <div class="mb-2">
        <h6 class="font-weight-bold mb-1">
          {{ $t('shipmentTo') }}
        </h6>
        <p
          v-if="project.google_maps_url"
          class="mb-0 small"
        >
          <a
            :href="project.google_maps_url"
            target="_blank"
          >Google Maps</a>
        </p>
        <p class="mb-0 small">
          {{ $t('address') }}: {{ project.address.formatted_address }}
        </p>
        <p
          class="mb-0 small"
          style="white-space: pre-line"
        >
          <strong>{{ $t('observations') }}:</strong>
          {{ project.observations || $t('noObservations') }}
        </p>
      </div>
      <hr>
      <template v-if="showDualPaymentOptions">
        <div class="d-flex justify-content-between mt-1">
          <span>{{ $t('cashAmountLabel') }}:</span>
          <span>${{ quote.cash_total_amount | money }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <span>{{ $t('creditAmountLabel') }}:</span>
          <span class="text-warning font-weight-bold">${{ quote.credit_total_amount | money }}</span>
        </div>
      </template>
      <template v-else-if="showSelectedCreditWithoutSurcharge">
        <div class="d-flex justify-content-between align-items-center">
          <span class="font-weight-bold">{{ $t('creditNoSurchargeAmountLabel') }}:</span>
          <span class="text-warning font-weight-bold h5">${{ selectedAmount | money }}</span>
        </div>
      </template>
      <div
        v-else
        class="d-flex justify-content-between align-items-center"
      >
        <span class="font-weight-bold">{{ selectedAmountLabel }}:</span>
        <span class="text-success font-weight-bold h5">${{ selectedAmount | money }}</span>
      </div>
    </b-card>

    <!-- Productos como tarjetas -->
    <invoice-products-table
      class="mb-3"
      :products="items"
      :supplier-data="supplierData"
      :project-data="project"
      :quote-data="quote"
      card-mode
      @delete-user="refetch"
      @items-updated="refetch"
    />

    <!-- Totales -->
    <b-card class="mb-3">
      <div class="d-flex justify-content-between mb-2">
        <span>{{ $t('subtotal') }}:</span>
        <span>${{ quote.subtotal | money }}</span>
      </div>
      <div class="d-flex justify-content-between mb-2">
        <span class="d-flex align-items-center">
          <span>{{ $t('shipping') }}:</span>
          <b-button
            v-if="canRemoveFreight"
            size="sm"
            variant="flat-danger"
            class="ml-1 px-50 py-25"
            :disabled="isRemovingFreight"
            @click="handleRemoveFreight"
          >
            <feather-icon icon="TrashIcon" size="14" />
          </b-button>
        </span>
        <span>${{ quote.shipping_total | money }}</span>
      </div>
      <div class="d-flex justify-content-between mb-2">
        <span>{{ $t('tax') }}:</span>
        <span>${{ quote.iva_total | money }}</span>
      </div>
      <div
        v-if="totalQuoteSavings > 0"
        class="quote-savings-total mb-2"
      >
        <span>Ahorro estimado</span>
        <strong>${{ totalQuoteSavings | money }}</strong>
        <small>vs. promedio de proveedores</small>
      </div>
      <template v-if="showCreditSurchargeBreakdown">
        <div class="d-flex justify-content-between mb-2">
          <strong>{{ $t('total') }}:</strong>
          <span>${{ quote.cash_total_amount | money }}</span>
        </div>
        <div class="d-flex justify-content-between mb-2">
          <span>{{ $t('creditSurchargeLabel') }}:</span>
          <span class="text-warning">${{ quote.credit_surcharge_total | money }}</span>
        </div>
        <hr>
        <div class="d-flex justify-content-between">
          <strong>{{ $t('creditFinalAmountLabel') }}:</strong>
          <span class="text-warning font-weight-bold">${{ quote.credit_total_amount | money }}</span>
        </div>
      </template>
      <template v-else-if="showDualPaymentOptions">
        <div class="d-flex justify-content-between mt-1">
          <span>{{ $t('cashAmountLabel') }}:</span>
          <span>${{ quote.cash_total_amount | money }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <span>{{ $t('creditAmountLabel') }}:</span>
          <span class="text-warning font-weight-bold">${{ quote.credit_total_amount | money }}</span>
        </div>
      </template>
      <div
        v-else
        class="d-flex justify-content-between"
      >
        <strong>{{ selectedAmountLabel }}:</strong>
        <strong :class="{ 'text-warning': showSelectedCreditWithoutSurcharge }">${{ selectedAmount | money }}</strong>
      </div>
    </b-card>

    <!-- Acciones -->
    <component
      :is="optionsBasedInRole"
      :key="`${optionsBasedInRole}-${roleName}`"
      :refetch="refetch"
      :quote="quote"
      :items="items"
      :project="project"
      :supplier-data="supplierData"
      class="mb-3"
    />

  </b-container>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Logo from '@/components/Logo.vue'
import InvoiceProductsTable from '@/components/invoice/InvoiceProductsTable.vue'
import CheckoutOptionsForAdmin from '@/components/invoice/CheckoutOptionsForAdmin.vue'
import CheckoutOptionsForUser from '@/components/invoice/CheckoutOptionsForUser.vue'

export default {
  components: {
    Logo,
    InvoiceProductsTable,
    CheckoutOptionsForAdmin,
    CheckoutOptionsForGuest: () => import('@/components/invoice/CheckoutOptionsForGuest.vue'),
    CheckoutOptionsForUser,
  },
  props: {
    quote: Object,
    project: Object,
    items: Array,
    supplierData: Array,
    taxInfo: Object,
    refetch: Function,
  },
  data() {
    return {
      isRemovingFreight: false,
    }
  },
  computed: {
    ...mapState('auth', ['currentUser']),
    roleName() {
      return this.currentUser?.role_name || 'guest'
    },
    hasRecordedPaymentSelection() {
      return Boolean(this.quote?.selected_payment_mode)
    },
    showDualPaymentOptions() {
      return Boolean(this.quote?.customer_credit_eligible) && !this.hasRecordedPaymentSelection
    },
    showSelectedCreditWithoutSurcharge() {
      return this.quote?.selected_payment_mode === 'credit' && !this.showCreditSurchargeBreakdown
    },
    showCreditSurchargeBreakdown() {
      return Number(this.quote?.credit_surcharge_total || 0) > 0
        || Boolean(this.quote?.credit_pricing_applied)
    },
    canRemoveFreight() {
      return this.roleName === 'admin'
        && Number(this.quote?.shipping_total || 0) > 0
        && !this.quote?.items_locked_for_accounting
    },
    selectedAmount() {
      return Number(this.quote?.selected_total_amount || this.quote?.total || 0)
    },
    selectedAmountLabel() {
      if (this.showSelectedCreditWithoutSurcharge) return this.$t('creditNoSurchargeAmountLabel')
      if (this.showCreditSurchargeBreakdown) return this.$t('creditFinalAmountLabel')
      return this.$t('total')
    },
    totalQuoteSavings() {
      return this.roundMoney((this.items || []).reduce((total, item) => (
        total + Math.max(this.getItemSavings(item), 0)
      ), 0))
    },
    displayTaxInformation() {
      return this.quote?.tax_information_attributes || this.quote?.tax_information || this.taxInfo || {}
    },
    taxAddress() {
      const info = this.displayTaxInformation
      const isValid = str => (typeof str === 'string' ? str : '')
      return `${isValid(info.street)}, ${isValid(info.ext_number)}, ${isValid(info.postal_code)}, ${isValid(info.city)}, ${isValid(info.state)}`
    },
    optionsBasedInRole() {
      const role = this.roleName
      if (role === 'guest') return 'CheckoutOptionsForGuest'
      if (role === 'admin') return 'CheckoutOptionsForAdmin'
      if (role === 'auctioneer') return 'CheckoutOptionsForAuctioneer'
      return 'CheckoutOptionsForUser'
    },
  },
  methods: {
    ...mapActions('quotes', ['removeFreight']),
    roundMoney(value) {
      return Math.round(Number(value || 0) * 100) / 100
    },
    getCustomerUnitPrice(item) {
      const explicitCustomerPrice = Number(item?.customer_unit_price)
      if (Number.isFinite(explicitCustomerPrice)) return explicitCustomerPrice

      return Number(item?.unit_price || 0) + Number(item?.unit_commission_price || 0)
    },
    resolveComparisonUnitPrice(option) {
      const candidates = [
        option?.customer_unit_price,
        option?.unit_price,
        option?.supplier_unit_price,
        option?.price,
      ]

      const found = candidates
        .map(value => Number(value))
        .find(value => Number.isFinite(value) && value > 0)

      return found || 0
    },
    normalizeSupplierKey(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
    },
    getSupplierLabel(item) {
      return item?.selected_supplier_branch_name
        || item?.selected_supplier_name
        || item?.supplier_name
        || item?.branch_name
        || item?.business_name
        || item?.supplier
        || ''
    },
    getItemSavings(item) {
      if (!item?.selected_supplier_branch_id || item?.active_status === false) return 0

      const unitPrices = Array.isArray(item?.unit_prices) ? item.unit_prices : []
      // Al cliente no le llegan las ofertas de cada proveedor; el backend le manda el ahorro ya calculado.
      if (unitPrices.length === 0) return this.roundMoney(Number(item?.estimated_savings) || 0)
      const selectedSupplierId = String(item.selected_supplier_branch_id || '')
      const selectedSupplierName = this.getSupplierLabel(item)
      const normalizedSelectedSupplierName = this.normalizeSupplierKey(selectedSupplierName)
      const selectedUnitPrice = this.roundMoney(this.getCustomerUnitPrice(item))
      const options = unitPrices
        .map(option => ({
          supplierId: option?.supplier_branch_id ? String(option.supplier_branch_id) : '',
          supplierName: option?.supplier_branch_name
            || option?.selected_supplier_branch_name
            || option?.supplier_name
            || option?.branch_name
            || option?.business_name
            || '',
          unitPrice: this.resolveComparisonUnitPrice(option),
        }))
        .filter(option => Number.isFinite(option.unitPrice) && option.unitPrice > 0)

      const hasSelectedOption = options.some(option => {
        const sameSupplierId = selectedSupplierId && option.supplierId === selectedSupplierId
        const sameSupplierName = normalizedSelectedSupplierName
          && this.normalizeSupplierKey(option.supplierName) === normalizedSelectedSupplierName
        const samePrice = this.roundMoney(option.unitPrice) === selectedUnitPrice
        return sameSupplierId || sameSupplierName || samePrice
      })

      if (!hasSelectedOption && selectedUnitPrice > 0) {
        options.push({
          supplierId: selectedSupplierId,
          supplierName: selectedSupplierName,
          unitPrice: selectedUnitPrice,
        })
      }

      if (options.length < 2 || selectedUnitPrice <= 0) return 0

      const participantPrices = options.map(option => option.unitPrice)
      const averageUnitPrice = participantPrices.reduce((sum, price) => sum + price, 0) / participantPrices.length
      const quantity = Number(item.quantity || 0)

      return this.roundMoney((averageUnitPrice - selectedUnitPrice) * quantity)
    },
    getErrorMessage(error, fallback = 'Ocurrió un error al procesar la solicitud.') {
      const payload = error?.data || error?.response?.data || {}
      if (Array.isArray(payload.messages) && payload.messages.length > 0) return payload.messages[0]
      if (typeof payload.error === 'string') return payload.error
      if (typeof error?.message === 'string') return error.message
      return fallback
    },
    async handleRemoveFreight() {
      if (this.isRemovingFreight || !this.canRemoveFreight) return
      if (!window.confirm('¿Deseas eliminar el flete de esta cotización?')) return

      try {
        this.isRemovingFreight = true
        await this.removeFreight(this.quote.id)
        if (typeof this.refetch === 'function') {
          await this.refetch()
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Flete eliminado de la cotización.',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.getErrorMessage(error, 'No fue posible eliminar el flete.'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isRemovingFreight = false
      }
    },
  },
}
</script>

<style scoped>
.checkout-mobile-wrapper {
  background-color: #f9f9f9;
}
.invoice-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.invoice-details p {
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}
.quote-savings-total {
  align-items: flex-end;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 6px;
  color: #047857;
  display: flex;
  flex-direction: column;
  font-weight: 800;
  padding: 0.65rem 0.75rem;
  text-align: right;
}
.quote-savings-total span {
  color: #065f46;
  font-size: 0.82rem;
  line-height: 1.15;
}
.quote-savings-total strong {
  color: #047857;
  font-size: 1.08rem;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  margin-top: 0.15rem;
  white-space: nowrap;
}
.quote-savings-total small {
  color: #059669;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.15;
  margin-top: 0.15rem;
}
</style>
